import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  CloudOff, FileText, Stethoscope, Calendar, ParkingCircle, 
  Users, Trash2, RefreshCw, HardDrive, Loader2, Cloud, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useOffline } from '@/contexts/OfflineContext';

const OFFLINE_SETTINGS_KEY = 'offlineCacheSettings';

const DEFAULT_CACHE_SETTINGS = {
  enabled: true,
  cacheDocuments: true,
  cacheMedical: true,
  cacheCalendar: true,
  cacheParking: true,
  cacheContacts: true,
};

/**
 * OfflineCacheSettings - Configure what data to cache for offline use
 */
const OfflineCacheSettings = () => {
  const { isOnline, isOfflineReady, clearCache, queuedRequests, syncing, syncStatus, syncAll } = useOffline();
  const [settings, setSettings] = useState(DEFAULT_CACHE_SETTINGS);
  const [clearing, setClearing] = useState(false);
  const [cacheSize, setCacheSize] = useState(null);

  // Load settings from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(OFFLINE_SETTINGS_KEY);
      if (stored) {
        setSettings({ ...DEFAULT_CACHE_SETTINGS, ...JSON.parse(stored) });
      }
    } catch (error) {
      console.error('Failed to load offline settings:', error);
    }
  }, []);

  // Estimate cache size
  useEffect(() => {
    const estimateCacheSize = async () => {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        try {
          const estimate = await navigator.storage.estimate();
          const usedMB = ((estimate.usage || 0) / (1024 * 1024)).toFixed(1);
          setCacheSize(usedMB);
        } catch (error) {
          console.error('Failed to estimate storage:', error);
        }
      }
    };
    estimateCacheSize();
  }, []);

  const handleToggle = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem(OFFLINE_SETTINGS_KEY, JSON.stringify(newSettings));
    
    // Update service worker with new settings
    if ('serviceWorker' in navigator && navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'UPDATE_CACHE_SETTINGS',
        settings: newSettings,
      });
    }
    
    toast.success('Offline settings updated');
  };

  const handleClearCache = async () => {
    setClearing(true);
    try {
      await clearCache();
      setCacheSize('0.0');
      toast.success('Offline cache cleared');
    } catch (error) {
      toast.error('Failed to clear cache');
    } finally {
      setClearing(false);
    }
  };

  const cacheTypes = [
    {
      key: 'cacheDocuments',
      icon: FileText,
      label: 'Documents',
      description: 'IDs, insurance cards, registrations',
      color: 'text-slate-600',
    },
    {
      key: 'cacheMedical',
      icon: Stethoscope,
      label: 'Medical Records',
      description: 'Medications, allergies, medical history',
      color: 'text-emerald-600',
    },
    {
      key: 'cacheCalendar',
      icon: Calendar,
      label: 'Calendar Events',
      description: 'Appointments and scheduled events',
      color: 'text-blue-600',
    },
    {
      key: 'cacheParking',
      icon: ParkingCircle,
      label: 'Parking Info',
      description: 'Active parking and saved places',
      color: 'text-purple-600',
    },
    {
      key: 'cacheContacts',
      icon: Users,
      label: 'Contacts',
      description: 'Saved contacts and business cards',
      color: 'text-amber-600',
    },
  ];

  return (
    <Card data-testid="offline-cache-settings">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CloudOff className={`h-5 w-5 ${isOfflineReady ? 'text-emerald-600' : 'text-slate-400'}`} />
            <div>
              <CardTitle>Offline Mode</CardTitle>
              <CardDescription>
                {isOfflineReady 
                  ? 'App data cached for offline access' 
                  : 'Service worker not available'}
              </CardDescription>
            </div>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(checked) => handleToggle('enabled', checked)}
            data-testid="offline-master-toggle"
          />
        </div>
      </CardHeader>

      {settings.enabled && (
        <CardContent className="space-y-4 pt-0">
          {/* Storage Info */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2">
              <HardDrive className="h-4 w-4 text-slate-500" />
              <span className="text-sm text-slate-600">
                {cacheSize !== null ? `${cacheSize} MB used` : 'Calculating...'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {isOnline ? (
                <Cloud className="h-4 w-4 text-emerald-500" />
              ) : (
                <CloudOff className="h-4 w-4 text-amber-500" />
              )}
              <span className="text-xs text-slate-500">
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
          </div>

          {/* Sync Status */}
          {(syncStatus?.hasPendingChanges || syncing) && (
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
              <div className="flex items-center gap-2">
                {syncing ? (
                  <RefreshCw className="h-4 w-4 text-amber-600 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 text-amber-600" />
                )}
                <span className="text-sm text-amber-700">
                  {syncing 
                    ? 'Syncing changes...' 
                    : `${syncStatus?.pendingOperations || 0} pending change${syncStatus?.pendingOperations !== 1 ? 's' : ''}`}
                </span>
              </div>
              {!syncing && isOnline && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={syncAll}
                  className="text-amber-700 hover:bg-amber-100"
                  data-testid="sync-now-btn"
                >
                  Sync Now
                </Button>
              )}
            </div>
          )}

          {/* Last Sync Time */}
          {syncStatus?.lastSyncTime && !syncStatus?.hasPendingChanges && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <CheckCircle className="h-3 w-3 text-emerald-500" />
              <span>Last synced: {new Date(syncStatus.lastSyncTime).toLocaleString()}</span>
            </div>
          )}

          {/* Data Types */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-slate-700">Cache These Items</h4>
            {cacheTypes.map((type) => {
              const Icon = type.icon;
              return (
                <div key={type.key} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Icon className={`h-4 w-4 ${type.color}`} />
                    <div>
                      <Label className="text-sm font-medium">{type.label}</Label>
                      <p className="text-xs text-slate-500">{type.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings[type.key]}
                    onCheckedChange={(checked) => handleToggle(type.key, checked)}
                    data-testid={`offline-toggle-${type.key}`}
                  />
                </div>
              );
            })}
          </div>

          {/* Clear Cache */}
          <div className="border-t pt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearCache}
              disabled={clearing}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="clear-offline-cache"
            >
              {clearing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Clear Offline Cache
            </Button>
            <p className="text-xs text-slate-500 mt-2">
              This will remove all cached data. You'll need an internet connection to reload.
            </p>
          </div>

          {/* Status */}
          {!isOnline && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
              <p className="text-sm text-amber-700">
                You're currently offline. Changes will sync when you're back online.
              </p>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default OfflineCacheSettings;
